import { useState, useEffect } from "react";
import { X, Download, FileText, Settings, Eye } from "lucide-react";
import { invoiceService } from "../../../services/invoiceService";

/**
 * Download Invoice Modal - 2025 UX Standard
 * 
 * Inspired by Stripe Billing and Linear
 * - Shows preview before download
 * - Allows safe PDF customization
 * - Explicit user action required
 * - Professional, calm, predictable
 */
export default function DownloadInvoiceModal({ invoice, onClose, onDownload, type = "invoice" }) {
  const [companySettings, setCompanySettings] = useState(null);
  const [options, setOptions] = useState({
    pageSize: 'A4',
    orientation: 'portrait',
    margins: 'normal',
    fontFamily: 'Inter',
    fontSize: 'normal',
    headingWeight: 'semibold',
    showGstBreakdown: true,
    showHsn: true,
    showQuantityUom: true,
    showItemDescription: true,
    showAmountInWords: true,
    accentColor: 'indigo',
    customColor: '#6366f1', // Default custom color
    dividerStyle: 'line',
    footerText: 'This is a computer-generated invoice and does not require a signature.',
    footerAlignment: 'center',
    thankYouMessage: 'Thank you for your business!',
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('options'); // 'preview' | 'options' - default to options per master prompt

  // Load default theme settings on mount
  useEffect(() => {
    loadThemeDefaults();
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      const theme = await invoiceService.getActiveTheme();
      setCompanySettings(theme?.company || null);
    } catch (error) {
      console.error('Failed to fetch company settings:', error);
    }
  };

  const loadThemeDefaults = async () => {
    try {
      // Try to load active theme settings
      if (window.api.invoiceTheme?.getActive) {
        const theme = await window.api.invoiceTheme.getActive();
        setOptions(prev => ({
          ...prev,
          pageSize: theme.pageSize || 'A4',
          showGstBreakdown: theme.showGstBreakdown !== false,
          showHsn: theme.showHsn !== false,
          fontFamily: theme.fontFamily || 'Inter',
          footerText: theme.footerNote || prev.footerText,
        }));
      }
    } catch (err) {
      // Use defaults if theme API not available
      console.log('Using default theme settings');
    }
  };

  const handleGenerateAndDownload = async () => {
    setIsGenerating(true);
    
    try {
      // Pass options to download handler
      await onDownload(options);
      
      // Close modal after successful download
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  // Check if invoice is editable
  const isLocked = invoice.status === 'paid' || invoice.status === 'cancelled';
  const isPartiallyLocked = invoice.status === 'sent';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-fade-in">
      {/* Modal Container */}
      <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">
        
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-slate-100 bg-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-lg">
              <FileText size={20} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">
                Download Invoice
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {invoice.invoiceNumber || invoice.invoice_number}
              </p>
            </div>
          </div>
          
          <button
            onClick={onClose}
            disabled={isGenerating}
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-8">
          <button
            onClick={() => setActiveTab('preview')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'preview'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Eye size={16} />
              Preview
            </div>
          </button>
          <button
            onClick={() => setActiveTab('options')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'options'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Settings size={16} />
              PDF Options
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {activeTab === 'preview' ? (
            <PreviewSection invoice={invoice} options={options} companySettings={companySettings} />
          ) : (
            <OptionsSection 
              options={options} 
              setOptions={setOptions}
              isLocked={isLocked}
              isPartiallyLocked={isPartiallyLocked}
            />
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-8 py-5 border-t border-slate-100 bg-slate-50">
          <div className="text-sm text-slate-600">
            {isLocked && (
              <span className="text-amber-600">
                ⚠️ Invoice is {invoice.status} - layout locked
              </span>
            )}
            {isPartiallyLocked && (
              <span className="text-blue-600">
                ℹ️ Invoice sent - line items locked
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={isGenerating}
              className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-white rounded-lg transition-colors border border-slate-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerateAndDownload}
              disabled={isGenerating}
              className="px-5 py-2.5 text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg transition-all shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Download size={16} />
              {isGenerating ? 'Generating PDF...' : 'Generate & Download PDF'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PREVIEW SECTION - USES REAL INVOICE DATA
   ═══════════════════════════════════════════════════════════ */

function PreviewSection({ invoice, options, companySettings }) {
  // Add error boundary and debugging
  if (!invoice) {
    return (
      <div className="p-8">
        <div className="text-center text-slate-500">
          <p>No invoice data available</p>
        </div>
      </div>
    );
  }

  if (!options) {
    return (
      <div className="p-8">
        <div className="text-center text-slate-500">
          <p>No options available</p>
        </div>
      </div>
    );
  }

  try {
    const items = invoice.items || [];
    const client = invoice.client;
  
  // Determine GST type
  const clientState = client?.state || "Maharashtra";
  const isSameState = clientState === "Maharashtra";
  
  // Use backend-calculated totals (single source of truth)
  const subtotal = invoice.subtotal || 0;
  const taxTotal = invoice.tax_total || 0;
  
  // Calculate discount amount based on type
  const discountValue = invoice.discount || 0;
  const discountType = invoice.discountType || invoice.discount_type || 'flat';
  const discountAmount = discountType === 'percentage' 
    ? (subtotal * discountValue) / 100 
    : discountValue;
  
  const shipping = invoice.shipping || 0;
  const roundOff = invoice.round_off || 0;
  
  // Calculate total manually to ensure it includes all adjustments
  const calculatedTotal = subtotal + taxTotal - discountAmount + shipping + roundOff;
  const total = calculatedTotal; // Use calculated total instead of stored total
  
  // GST breakdown from stored backend data
  let cgstTotal = 0, sgstTotal = 0, igstTotal = 0;
  items.forEach(item => {
    cgstTotal += Number(item.cgst_amount) || 0;
    sgstTotal += Number(item.sgst_amount) || 0;
    igstTotal += Number(item.igst_amount) || 0;
  });
  
  // Accent color mapping
  const accentColors = {
    gray: '#64748b',
    indigo: '#6366f1',
    blue: '#3b82f6',
    emerald: '#10b981',
    custom: options.customColor, // Use custom color value
  };
  const brandColor = accentColors[options.accentColor] || accentColors.indigo;
  
  // Font size mapping
  const fontSizes = {
    small: { base: '10px', heading: '20px', invoice: '28px' },
    normal: { base: '11px', heading: '24px', invoice: '32px' },
    large: { base: '12px', heading: '28px', invoice: '36px' },
  };
  const fontSize = fontSizes[options.fontSize] || fontSizes.normal;
  
  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <h3 className="text-sm font-semibold text-slate-700">Live Preview</h3>
        </div>
        <p className="text-sm text-slate-500">
          This is how your PDF will look with current settings
        </p>
      </div>

      {/* Preview Container - Mimics PDF appearance */}
      <div className="bg-white border-2 border-slate-200 rounded-xl shadow-lg overflow-hidden">
        <div 
          className="bg-white p-8 overflow-auto"
          style={{ 
            aspectRatio: options.pageSize === 'Letter' ? '1/1.294' : '1/1.414',
            fontSize: fontSize.base,
          }}
        >
          {/* Real Invoice Preview */}
          <div className="max-w-2xl mx-auto space-y-6">
            
            {/* Header */}
            <div 
              className="flex justify-between items-start pb-4"
              style={{ 
                borderBottom: options.dividerStyle === 'line' ? '2px solid #e2e8f0' : 
                             options.dividerStyle === 'soft' ? '1px solid #f1f5f9' : 'none' 
              }}
            >
              <div>
                <div 
                  className={`font-bold text-slate-900 mb-2`}
                  style={{ 
                    fontSize: fontSize.heading,
                    fontWeight: options.headingWeight === 'medium' ? 500 : 600,
                    color: brandColor,
                  }}
                >
                  {companySettings?.name || 'ZyloBill'}
                </div>
                <div className="text-xs text-slate-600 space-y-0.5">
                  {companySettings?.address && <div>{companySettings.address}</div>}
                  {(companySettings?.city || companySettings?.state || companySettings?.pincode) && (
                    <div>
                      {[companySettings.city, companySettings.state, companySettings.pincode]
                        .filter(Boolean)
                        .join(', ')}
                    </div>
                  )}
                  {companySettings?.gstin && <div>GSTIN: {companySettings.gstin}</div>}
                </div>
              </div>
              <div className="text-right">
                <div 
                  className="font-bold text-slate-900 mb-2"
                  style={{ 
                    fontSize: fontSize.invoice,
                    fontWeight: options.headingWeight === 'medium' ? 500 : 600,
                    color: brandColor,
                  }}
                >
                  INVOICE
                </div>
                <div className="text-sm font-semibold text-slate-700">
                  {invoice.invoiceNumber || invoice.invoice_number}
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  <div>Issue: {formatDate(invoice.issueDate || invoice.issue_date)}</div>
                  <div>Due: {formatDate(invoice.dueDate || invoice.due_date)}</div>
                </div>
              </div>
            </div>

            {/* Bill To */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <div className="text-xs font-semibold text-slate-500 uppercase mb-2">
                Bill To
              </div>
              <div className="text-sm font-semibold text-slate-900">
                {client?.name || 'No client selected'}
              </div>
              {client && (
                <div className="text-xs text-slate-600 mt-1">
                  {client.city || 'City'}, {client.state || 'State'}
                  {client.gstin && <div className="mt-1">GSTIN: {client.gstin}</div>}
                </div>
              )}
            </div>

            {/* Items Table Preview */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Item</th>
                    {options.showHsn && (
                      <th className="text-center py-2 px-3 font-semibold text-slate-700">HSN</th>
                    )}
                    {options.showQuantityUom && (
                      <th className="text-center py-2 px-3 font-semibold text-slate-700">Qty</th>
                    )}
                    <th className="text-right py-2 px-3 font-semibold text-slate-700">Rate</th>
                    {options.showGstBreakdown && isSameState && (
                      <>
                        <th className="text-right py-2 px-3 font-semibold text-slate-700">CGST</th>
                        <th className="text-right py-2 px-3 font-semibold text-slate-700">SGST</th>
                      </>
                    )}
                    {options.showGstBreakdown && !isSameState && (
                      <th className="text-right py-2 px-3 font-semibold text-slate-700">IGST</th>
                    )}
                    <th className="text-right py-2 px-3 font-semibold text-slate-700">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length > 0 ? items.slice(0, 3).map((item, idx) => (
                    <tr key={idx} className="border-t border-slate-100">
                      <td className="py-2 px-3 text-slate-900">
                        <div className="font-medium">{item.name}</div>
                        {options.showItemDescription && item.description && (
                          <div className="text-[10px] text-slate-500 mt-0.5">{item.description}</div>
                        )}
                      </td>
                      {options.showHsn && (
                        <td className="py-2 px-3 text-center text-slate-600">
                          {item.hsn || item.sac || '—'}
                        </td>
                      )}
                      {options.showQuantityUom && (
                        <td className="py-2 px-3 text-center text-slate-600">
                          {item.quantity} {item.uom || 'Nos'}
                        </td>
                      )}
                      <td className="py-2 px-3 text-right text-slate-600">
                        ₹{formatCurrency(item.rate)}
                      </td>
                      {options.showGstBreakdown && isSameState && (
                        <>
                          <td className="py-2 px-3 text-right text-slate-600">
                            ₹{formatCurrency(item.cgst_amount || 0)}
                          </td>
                          <td className="py-2 px-3 text-right text-slate-600">
                            ₹{formatCurrency(item.sgst_amount || 0)}
                          </td>
                        </>
                      )}
                      {options.showGstBreakdown && !isSameState && (
                        <td className="py-2 px-3 text-right text-slate-600">
                          ₹{formatCurrency(item.igst_amount || 0)}
                        </td>
                      )}
                      <td className="py-2 px-3 text-right font-semibold text-slate-900">
                        ₹{formatCurrency(item.amount || 0)}
                      </td>
                    </tr>
                  )) : (
                    <tr className="border-t border-slate-100">
                      <td colSpan="8" className="py-4 px-3 text-center text-slate-400 italic">
                        No items added yet
                      </td>
                    </tr>
                  )}
                  {items.length > 3 && (
                    <tr className="border-t border-slate-100">
                      <td colSpan="8" className="py-2 px-3 text-center text-xs text-slate-400">
                        ... and {items.length - 3} more items
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 bg-slate-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="font-semibold text-slate-900">₹{formatCurrency(subtotal)}</span>
                </div>
                {options.showGstBreakdown ? (
                  isSameState ? (
                    <>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-600">CGST</span>
                        <span className="font-semibold text-slate-900">₹{formatCurrency(cgstTotal)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-600">SGST</span>
                        <span className="font-semibold text-slate-900">₹{formatCurrency(sgstTotal)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-600">IGST</span>
                      <span className="font-semibold text-slate-900">₹{formatCurrency(igstTotal)}</span>
                    </div>
                  )
                ) : (
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-600">Tax</span>
                    <span className="font-semibold text-slate-900">₹{formatCurrency(taxTotal)}</span>
                  </div>
                )}
                {discountAmount > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-600">Discount</span>
                    <span className="font-semibold text-red-600">-₹{formatCurrency(discountAmount)}</span>
                  </div>
                )}
                {shipping > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-600">Shipping</span>
                    <span className="font-semibold text-slate-900">+₹{formatCurrency(shipping)}</span>
                  </div>
                )}
                {roundOff !== 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-600">Round Off</span>
                    <span className="font-semibold text-slate-900">
                      {roundOff > 0 ? '+' : ''}₹{formatCurrency(Math.abs(roundOff))}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-bold pt-2 border-t-2 border-slate-300">
                  <span className="text-slate-900">Total</span>
                  <span className="text-slate-900">₹{formatCurrency(total)}</span>
                </div>
              </div>
            </div>

            {/* Amount in Words */}
            {options.showAmountInWords && (
              <div className="bg-slate-50 p-3 rounded-lg border-l-4" style={{ borderColor: brandColor }}>
                <div className="text-[10px] font-semibold text-slate-500 uppercase mb-1">
                  Amount in Words
                </div>
                <div className="text-xs font-semibold text-slate-900">
                  {numberToWordsSimple(Math.floor(total))} rupees only
                </div>
              </div>
            )}

            {/* Footer Note */}
            <div 
              className="pt-4 border-t border-slate-200"
              style={{ textAlign: options.footerAlignment }}
            >
              <p className="text-xs text-slate-500 italic">
                {options.footerText}
              </p>
              {options.thankYouMessage && (
                <p className="text-xs text-slate-400 mt-2">
                  {options.thankYouMessage}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex gap-3">
          <div className="text-blue-600 mt-0.5">
            <Eye size={16} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-blue-900">
              Preview updates in real-time
            </div>
            <div className="text-xs text-blue-700 mt-1">
              Switch to "PDF Options" tab to customize layout, fonts, and footer text.
              Changes will reflect here instantly.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  } catch (error) {
    console.error('PreviewSection error:', error);
    return (
      <div className="p-8">
        <div className="text-center text-red-500">
          <p>Error rendering preview: {error.message}</p>
        </div>
      </div>
    );
  }
}

/* ═══════════════════════════════════════════════════════════
   OPTIONS SECTION - ENHANCED WITH ALL MASTER PROMPT REQUIREMENTS
   ═══════════════════════════════════════════════════════════ */

function OptionsSection({ options, setOptions, isLocked, isPartiallyLocked }) {
  return (
    <div className="p-8">
      <div className="max-w-2xl mx-auto space-y-8">
        
        {/* Page Settings */}
        <OptionGroup
          title="Page Settings"
          description="Configure page size, orientation, and margins"
        >
          <OptionField label="Page Size">
            <select
              value={options.pageSize}
              onChange={(e) => setOptions({ ...options, pageSize: e.target.value })}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 disabled:cursor-not-allowed text-sm"
            >
              <option value="A4">A4 (210 × 297 mm) - India Standard</option>
              <option value="Letter">Letter (8.5 × 11 in) - US Standard</option>
            </select>
          </OptionField>

          <OptionField label="Orientation">
            <select
              value={options.orientation}
              onChange={(e) => setOptions({ ...options, orientation: e.target.value })}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 disabled:cursor-not-allowed text-sm"
            >
              <option value="portrait">Portrait (Default)</option>
              <option value="landscape">Landscape</option>
            </select>
          </OptionField>

          <OptionField label="Margins">
            <select
              value={options.margins}
              onChange={(e) => setOptions({ ...options, margins: e.target.value })}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 disabled:cursor-not-allowed text-sm"
            >
              <option value="compact">Compact - More content per page</option>
              <option value="normal">Normal - Balanced spacing</option>
              <option value="spacious">Spacious - Extra breathing room</option>
            </select>
          </OptionField>
        </OptionGroup>

        {/* Typography */}
        <OptionGroup
          title="Typography"
          description="Customize fonts and text styling"
        >
          <OptionField label="Font Family">
            <select
              value={options.fontFamily}
              onChange={(e) => setOptions({ ...options, fontFamily: e.target.value })}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 disabled:cursor-not-allowed text-sm"
            >
              <option value="Inter">Inter - Modern, Default</option>
              <option value="Roboto">Roboto - Professional</option>
              <option value="Serif">Source Serif - Traditional</option>
            </select>
          </OptionField>

          <OptionField label="Font Size">
            <select
              value={options.fontSize}
              onChange={(e) => setOptions({ ...options, fontSize: e.target.value })}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 disabled:cursor-not-allowed text-sm"
            >
              <option value="small">Small - Compact layout</option>
              <option value="normal">Normal - Recommended</option>
              <option value="large">Large - Better readability</option>
            </select>
          </OptionField>

          <OptionField label="Heading Weight">
            <select
              value={options.headingWeight}
              onChange={(e) => setOptions({ ...options, headingWeight: e.target.value })}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 disabled:cursor-not-allowed text-sm"
            >
              <option value="medium">Medium - Subtle emphasis</option>
              <option value="semibold">Semibold - Strong emphasis</option>
            </select>
          </OptionField>
        </OptionGroup>

        {/* Layout Options */}
        <OptionGroup
          title="Layout Options"
          description="Control what appears in your PDF"
        >
          <OptionToggle
            label="Show GST Breakdown"
            description="Display CGST/SGST/IGST columns and totals separately"
            checked={options.showGstBreakdown}
            onChange={(checked) => setOptions({ ...options, showGstBreakdown: checked })}
            disabled={isPartiallyLocked || isLocked}
            lockedReason={isPartiallyLocked ? "Tax structure locked after sending" : undefined}
          />

          <OptionToggle
            label="Show HSN/SAC Column"
            description="Display HSN/SAC codes in the items table"
            checked={options.showHsn}
            onChange={(checked) => setOptions({ ...options, showHsn: checked })}
            disabled={isPartiallyLocked || isLocked}
            lockedReason={isPartiallyLocked ? "Item structure locked after sending" : undefined}
          />

          <OptionToggle
            label="Show Quantity & UoM"
            description="Display quantity and unit of measurement"
            checked={options.showQuantityUom}
            onChange={(checked) => setOptions({ ...options, showQuantityUom: checked })}
            disabled={isPartiallyLocked || isLocked}
            lockedReason={isPartiallyLocked ? "Item structure locked after sending" : undefined}
          />

          <OptionToggle
            label="Show Item Description"
            description="Display detailed item descriptions below item names"
            checked={options.showItemDescription}
            onChange={(checked) => setOptions({ ...options, showItemDescription: checked })}
            disabled={isLocked}
          />

          <OptionToggle
            label="Show Amount in Words"
            description="Display total amount written in words"
            checked={options.showAmountInWords}
            onChange={(checked) => setOptions({ ...options, showAmountInWords: checked })}
            disabled={isLocked}
          />
        </OptionGroup>

        {/* Branding */}
        <OptionGroup
          title="Branding"
          description="Customize colors and visual style"
        >
          <OptionField label="Accent Color">
            <div className="space-y-2">
              {[
                { value: 'gray', label: 'Neutral Gray', desc: 'Professional', color: '#64748b' },
                { value: 'indigo', label: 'Indigo', desc: 'Modern (Default)', color: '#6366f1' },
                { value: 'blue', label: 'Blue', desc: 'Corporate', color: '#3b82f6' },
                { value: 'emerald', label: 'Emerald', desc: 'Fresh', color: '#10b981' },
              ].map((colorOption) => (
                <label
                  key={colorOption.value}
                  className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    options.accentColor === colorOption.value
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  } ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="radio"
                    name="accentColor"
                    value={colorOption.value}
                    checked={options.accentColor === colorOption.value}
                    onChange={(e) => !isLocked && setOptions({ ...options, accentColor: e.target.value })}
                    disabled={isLocked}
                    className="sr-only"
                  />
                  <div
                    className="w-8 h-8 rounded-lg border-2 border-white shadow-sm flex-shrink-0"
                    style={{ backgroundColor: colorOption.color }}
                  ></div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900">
                      {colorOption.label}
                    </div>
                    <div className="text-xs text-slate-500">
                      {colorOption.desc}
                    </div>
                  </div>
                  {options.accentColor === colorOption.value && (
                    <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </label>
              ))}
              
              {/* Custom Color Option */}
              <label
                className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                  options.accentColor === 'custom'
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                } ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <input
                  type="radio"
                  name="accentColor"
                  value="custom"
                  checked={options.accentColor === 'custom'}
                  onChange={(e) => !isLocked && setOptions({ ...options, accentColor: e.target.value })}
                  disabled={isLocked}
                  className="sr-only"
                />
                <div className="relative w-8 h-8 rounded-lg border-2 border-white shadow-sm flex-shrink-0 overflow-hidden">
                  <input
                    type="color"
                    value={options.customColor}
                    onChange={(e) => {
                      setOptions({ ...options, customColor: e.target.value, accentColor: 'custom' });
                    }}
                    disabled={isLocked}
                    className="absolute inset-0 w-full h-full cursor-pointer border-0"
                    style={{ padding: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-900">
                    Custom Color
                  </div>
                  <div className="text-xs text-slate-500">
                    Choose your own color
                  </div>
                </div>
                {options.accentColor === 'custom' && (
                  <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </label>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Used for headings and highlights only - does not affect calculations
            </p>
          </OptionField>

          <OptionField label="Divider Style">
            <select
              value={options.dividerStyle}
              onChange={(e) => setOptions({ ...options, dividerStyle: e.target.value })}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 disabled:cursor-not-allowed text-sm"
            >
              <option value="line">Line - Clear separation</option>
              <option value="soft">Soft - Subtle separation</option>
              <option value="none">None - Minimal design</option>
            </select>
          </OptionField>
        </OptionGroup>

        {/* Footer Customization */}
        <OptionGroup
          title="Footer"
          description="Customize footer text and alignment"
        >
          <OptionField label="Footer Text">
            <textarea
              value={options.footerText}
              onChange={(e) => setOptions({ ...options, footerText: e.target.value })}
              disabled={isLocked}
              rows={3}
              maxLength={200}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 disabled:cursor-not-allowed text-sm"
              placeholder="This is a computer-generated invoice..."
            />
            <p className="text-xs text-slate-500 mt-1">
              Main footer text (max 200 characters)
            </p>
          </OptionField>

          <OptionField label="Footer Alignment">
            <select
              value={options.footerAlignment}
              onChange={(e) => setOptions({ ...options, footerAlignment: e.target.value })}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 disabled:cursor-not-allowed text-sm"
            >
              <option value="center">Center - Balanced</option>
              <option value="left">Left - Formal</option>
              <option value="right">Right - Modern</option>
            </select>
          </OptionField>

          <OptionField label="Thank You Message (Optional)">
            <input
              type="text"
              value={options.thankYouMessage}
              onChange={(e) => setOptions({ ...options, thankYouMessage: e.target.value })}
              disabled={isLocked}
              maxLength={100}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 disabled:cursor-not-allowed text-sm"
              placeholder="Thank you for your business!"
            />
            <p className="text-xs text-slate-500 mt-1">
              Optional closing message (max 100 characters)
            </p>
          </OptionField>
        </OptionGroup>

        {/* Safety Notice - Stripe Style */}
        <div className="p-5 bg-gradient-to-br from-slate-50 to-slate-100/50 border border-slate-200 rounded-xl">
          <div className="flex gap-3">
            <div className="text-slate-600 mt-0.5">
              <Settings size={18} />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900 mb-2">
                Safe Customization Only
              </div>
              <div className="text-xs text-slate-600 space-y-1.5 leading-relaxed">
                <div className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>These options only affect PDF appearance and layout</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>All calculations and totals remain unchanged</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Legal compliance and tax structure maintained</span>
                </div>
                {isPartiallyLocked && (
                  <div className="flex items-start gap-2 mt-3 pt-3 border-t border-slate-200">
                    <span className="text-amber-600 mt-0.5">⚠</span>
                    <span className="text-amber-700 font-medium">
                      Some options locked because invoice is sent
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   HELPER COMPONENTS
   ═══════════════════════════════════════════════════════════ */

function OptionGroup({ title, description, children }) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <p className="text-xs text-slate-500 mt-1">{description}</p>
      </div>
      <div className="space-y-4 pl-4 border-l-2 border-slate-200">
        {children}
      </div>
    </div>
  );
}

function OptionField({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}

function OptionToggle({ label, description, checked, onChange, disabled, lockedReason }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} group`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => !disabled && onChange(e.target.checked)}
        disabled={disabled}
        className="mt-1 w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 disabled:cursor-not-allowed"
      />
      <div className="flex-1">
        <div className="text-sm font-medium text-slate-900 group-hover:text-indigo-600 transition-colors">
          {label}
          {disabled && lockedReason && (
            <span className="ml-2 text-xs text-amber-600">🔒 {lockedReason}</span>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          {description}
        </div>
      </div>
    </label>
  );
}

/* ═══════════════════════════════════════════════════════════
   UTILITIES
   ═══════════════════════════════════════════════════════════ */

function formatDate(dateString) {
  if (!dateString) return "—";
  try {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function formatCurrency(value) {
  const num = Number(value) || 0;
  // Smart formatting: show decimals only when needed
  if (num % 1 === 0) {
    return num.toLocaleString('en-IN');
  }
  return num.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function numberToWordsSimple(num) {
  if (!num || num === 0) return "Zero";
  
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];

  function convertLessThanThousand(n) {
    if (n === 0) return "";
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
    return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + convertLessThanThousand(n % 100) : "");
  }

  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const remainder = Math.floor(num % 1000);

  let result = "";
  if (crore) result += convertLessThanThousand(crore) + " Crore ";
  if (lakh) result += convertLessThanThousand(lakh) + " Lakh ";
  if (thousand) result += convertLessThanThousand(thousand) + " Thousand ";
  if (remainder) result += convertLessThanThousand(remainder);

  return result.trim();
}
